import React, { useState, useEffect, useRef } from 'react';
import { Input, Button, Box } from '@mui/joy';
import { productService } from '../api/product';
import { Product, ProductCreateDTO } from '../api/product';
import { Edit, Trash, Plus, GripVertical } from 'lucide-react';
import { PAGE_TRANSLATIONS } from '../conf';

const ProductPage: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [newProduct, setNewProduct] = useState<ProductCreateDTO>({ name: '', description: '' });
  const [nameError, setNameError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [draggedItemIndex, setDraggedItemIndex] = useState<number | null>(null);
  const [isDraggingOver, setIsDraggingOver] = useState<number | null>(null);
  const fetchInitiatedRef = useRef(false);

  useEffect(() => {
    if (fetchInitiatedRef.current) {
      return;
    }
    fetchInitiatedRef.current = true;

    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await productService.list({ sort: 'seq', order: 'ASC' });
        setProducts(response.data.data);
      } catch (error) {
        console.error('Failed to fetch products', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProducts();
  }, []);

  const handleSave = async (product: Product | ProductCreateDTO) => {
    if (!product.name.trim()) {
      if ('id' in product) {
        setEditingProduct({ ...product, name: '' });
      } else {
        setNameError(true);
      }
      return;
    }

    try {
      if ('id' in product) {
        await productService.update(product.id, product);
        setProducts(products.map(p => (p.id === product.id ? product : p)));
      } else {
        const maxSeq = products.length > 0 ? Math.max(...products.map(p => p.seq)) : -1;
        const productWithSeq = { ...product, seq: maxSeq + 1 };
        const response = await productService.create(productWithSeq);
        setProducts([...products, response.data.data]);
        setNewProduct({ name: '', description: '' });
        setNameError(false);
      }
      setEditingProduct(null);
    } catch (error) {
      console.error('Failed to save product', error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewProduct({ ...newProduct, [name]: value });
    
    if (name === 'name' && value.trim()) {
      setNameError(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm(PAGE_TRANSLATIONS.DELETE_CONFIRM_PRODUCT)) {
      try {
        await productService.delete(id);
        
        const remainingProducts = products.filter(p => p.id !== id);
        const updatedProducts = reorderProductsAfterDelete(remainingProducts);
        await updateProductSequences(updatedProducts);
        setProducts(updatedProducts);
      } catch (error) {
        console.error('Failed to delete product', error);
      }
    }
  };

  const reorderProductsAfterDelete = (productList: Product[]): Product[] => {
    return productList.map((product, index) => ({
      ...product,
      seq: index
    }));
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
    setDraggedItemIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault(); 
    if (draggedItemIndex !== null && draggedItemIndex !== index) {
        setIsDraggingOver(index);
    } else {
        setIsDraggingOver(null);
    }
  };
  
  const handleDragLeave = () => {
    setIsDraggingOver(null);
  };

  const handleDrop = async (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    setIsDraggingOver(null);
    if (draggedItemIndex === null || draggedItemIndex === dropIndex) {
      setDraggedItemIndex(null);
      return;
    }

    const reorderedProducts = Array.from(products);
    const [removed] = reorderedProducts.splice(draggedItemIndex, 1);
    reorderedProducts.splice(dropIndex, 0, removed);
    const updatedProducts = reorderedProducts.map((product, index) => ({ ...product, seq: index }));
    setProducts(updatedProducts);
    setDraggedItemIndex(null);
    await updateProductSequences(updatedProducts);
  };

  const handleDragEnd = () => {
    setDraggedItemIndex(null);
    setIsDraggingOver(null);
  };

  const updateProductSequences = async (productList: Product[]) => {
    try {
      setLoading(true);
      const orderedProductIds = productList.map(product => product.id);
      await productService.updateSequence(orderedProductIds);
    } catch (error) {
      console.error('Failed to update product sequences', error);
    } finally {
      setLoading(false);
    }
  };

  const getDraggableItemStyle = (index: number): React.CSSProperties => {
    const isBeingDragged = draggedItemIndex === index;
    const isDragTarget = isDraggingOver === index;

    return {
        display: 'flex',
        alignItems: 'center',
        padding: '10px 0',
        borderBottom: '1px solid #e0e0e0',
        background: isBeingDragged ? '#e3f2fd' : (isDragTarget ? '#eeeeee' : 'white'),
        opacity: loading ? 0.5 : 1,
        cursor: editingProduct ? 'default' : 'grab',
        transition: 'background-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease',
        transform: isBeingDragged ? 'scale(1.02)' : 'scale(1)',
        boxShadow: isBeingDragged ? '0 4px 8px rgba(0,0,0,0.1)' : 'none',
        position: 'relative',
        zIndex: isBeingDragged ? 1 : 0,
    };
  };

  return (
    <div>
      <h2>{PAGE_TRANSLATIONS.PRODUCTS}</h2>
      
      <div className="product-table">
        <div className="product-table-header" style={{ display: 'flex', fontWeight: 'bold', borderBottom: '1px solid #cccccc', padding: '12px 0', marginBottom: '5px' }}>
          <div style={{ width: '50px' }}></div>
          <div style={{ flex: 2 }}>{PAGE_TRANSLATIONS.NAME}</div>
          <div style={{ flex: 3 }}>{PAGE_TRANSLATIONS.DESCRIPTION}</div>
          <div style={{ width: '120px' }}>{PAGE_TRANSLATIONS.ACTIONS}</div>
        </div>
        
        <div style={{ minHeight: '100px' }}>
          {products.map((product, index) => (
            <div
              key={product.id.toString()}
              draggable={!editingProduct}
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
              style={getDraggableItemStyle(index)}
            >
              <div style={{ width: '50px', textAlign: 'center' }}>
                <Box
                  sx={{
                    cursor: editingProduct ? 'default' : 'grab',
                    display: 'inline-flex',
                    justifyContent: 'center',
                    padding: '6px',
                    borderRadius: '4px',
                    backgroundColor: '#f0f0f0',
                    transition: 'background-color 0.2s',
                    opacity: editingProduct ? 0.5 : 1,
                    '&:hover': {
                      backgroundColor: editingProduct ? '#f0f0f0' : '#e0e0e0',
                    }
                  }}
                >
                  <GripVertical size={16} />
                </Box>
              </div>
              
              <div style={{ flex: 2 }}>
                {editingProduct?.id === product.id ? (
                  <Input 
                    size="sm"
                    value={editingProduct.name}
                    onChange={(e) => setEditingProduct({ ...editingProduct, name: e.target.value })}
                    error={editingProduct.name.trim() === ''}
                  />
                ) : (
                  product.name
                )}
              </div>
              
              <div style={{ flex: 3 }}>
                {editingProduct?.id === product.id ? (
                  <Input 
                    size="sm" 
                    value={editingProduct.description || ''} 
                    onChange={(e) => setEditingProduct({ ...editingProduct, description: e.target.value })} 
                  />
                ) : (
                  product.description
                )}
              </div>
              
              <div style={{ width: '120px', textAlign: 'right' }}>
                {editingProduct?.id === product.id ? (
                  <>
                    <Button size="sm" variant="plain" onClick={() => handleSave(editingProduct)}>{PAGE_TRANSLATIONS.SAVE}</Button>
                    <Button size="sm" variant="plain" onClick={() => setEditingProduct(null)}>{PAGE_TRANSLATIONS.CANCEL}</Button>
                  </>
                ) : (
                  <>
                    <Button size="sm" variant="plain" onClick={() => setEditingProduct(product)}><Edit size={16} /></Button>
                    <Button size="sm" variant="plain" onClick={() => handleDelete(product.id)}><Trash size={16} /></Button>
                  </>
                )}
              </div>
            </div>
          ))}
          
          <div style={{ display: 'flex', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #e0e0e0' }}>
            <div style={{ width: '50px' }}></div>
            <div style={{ flex: 2 }}>
              <Input 
                size="sm"
                placeholder={`${PAGE_TRANSLATIONS.NAME} *`} 
                value={newProduct.name} 
                onChange={(e) => handleChange({ ...e, target: { ...e.target, name: 'name' } })}
                error={nameError}
                name="name"
              />
            </div>
            <div style={{ flex: 3 }}>
              <Input 
                size="sm"
                placeholder={PAGE_TRANSLATIONS.DESCRIPTION} 
                value={newProduct.description || ''} 
                onChange={(e) => handleChange({ ...e, target: { ...e.target, name: 'description' } })}
                name="description"
              />
            </div>
            <div style={{ width: '120px', textAlign: 'right' }}>
              <Button size="sm" onClick={() => handleSave(newProduct)}><Plus size={16} /></Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductPage; 